import { AxiosHeaderValue, AxiosInstance, AxiosRequestConfig, AxiosResponse, isAxiosError, isCancel } from "axios"
import _ from 'lodash'
import { FusionError, serializeFusionError } from '@fusion-advantage/backcompat'
import { Result, err, ok } from "neverthrow"

export interface SyncHttpReqConfig {
	axInst: AxiosInstance
	method: 'GET'|'POST'|'PUT'
	url: string
	data?: any
	ifNoneMatch?: string|null
	headers?: AxiosRequestConfig["headers"]
	timeout?: number
}

export enum SyncHttpReqResultStatus {
	Ok = 'Ok',
	NotModified = 'NotModified',
	// the api deliberately rejected the request, as opposed to failing;
	// callers that can explain a rejection to the user should handle this,
	// everyone else can leave it to fall in with Unknown
	BadRequest = 'BadRequest',
	Unauthorized = 'Unauthorized',
	Forbidden = 'Forbidden',
	NetworkError = 'NetworkError',
	Unknown = 'Unknown',
}

export type SyncHttpReqResultOk = {
	data: ArrayBuffer
	etag: string|null
	contentType: string|null
	axResp: AxiosResponse<ArrayBuffer>
}

export type SyncHttpReqResultError = {
	status: (
		SyncHttpReqResultStatus.NotModified|
		SyncHttpReqResultStatus.Unauthorized|
		SyncHttpReqResultStatus.Forbidden|
		SyncHttpReqResultStatus.NetworkError
	)
	error?: never
} | {
	status: (
		SyncHttpReqResultStatus.BadRequest|
		SyncHttpReqResultStatus.Unknown
	)
	error: FusionError
}

export type SyncHttpReqResult = Result<SyncHttpReqResultOk, SyncHttpReqResultError>

export const syncHttpReq = async (req: SyncHttpReqConfig): Promise<SyncHttpReqResult> => {
	// 20260731 timeout was 15000
	// but the unfiltered icd proc currently takes ~20s
	const { axInst, method, url, data, ifNoneMatch, headers, timeout = 30000 } = req
	const cancel = createCancel(timeout)
	const reqConfig: AxiosRequestConfig = {
		method,
		url,
		data,
		headers,
		responseType: 'arraybuffer',
		// 20260402 add idle (no data tx) timeout to error out bad connections
		signal: cancel.signal,
		onDownloadProgress: () => cancel.reset(),
		onUploadProgress: () => cancel.reset(),
	}
	if(ifNoneMatch) {
		reqConfig.headers = {
			...(headers ?? {}),
			'If-None-Match': ifNoneMatch,
		}
	}
	reqConfig.validateStatus = (status) => {
		if(status >= 200 && status < 300) {
			return true
		}
		if(status === 400 || status === 401 || status === 403) {
			// handled below explicitly
			return true
		}
		if(ifNoneMatch && status === 304) {
			// now expected because a hash to check has been provided
			return true
		}
		return false
	}
	let axResp: AxiosResponse
	try {
		axResp = await axInst.request(reqConfig)
	} catch(ex) {
		if(isCancel(ex)) {
			return err({
				status: SyncHttpReqResultStatus.NetworkError,
			})
		}
		// if the call to validateStatus is false, the error is handled here
		if(isAxiosError(ex)) {
			if(
				ex.code === 'ERR_NETWORK' ||  // generic in-browser network error
				ex.code === 'ECONNRESET' ||   // node/vitest
				ex.code === 'ECONNABORTED' ||  // node/vitest request timeout
				String(ex) === 'TypeError: Network error'  // also seen in vitest, not completely sure why
			) {
				return err({
					status: SyncHttpReqResultStatus.NetworkError,
				})
			}
		}
		return err({
			status: SyncHttpReqResultStatus.Unknown,
			error: serializeFusionError('syncHttpReq', ex)
		})
	}
	// currently axios in vitest spitting out a Uint8Array despite the
	// request config above, fix it
	if(import.meta.env.VITEST && ArrayBuffer.isView(axResp.data)) {
		axResp.data = axResp.data.buffer
	}
	if(!(axResp.data instanceof ArrayBuffer)) {
		return err({
			status: SyncHttpReqResultStatus.Unknown,
			error: serializeFusionError('syncHttpReq', '!(axResp.data instanceof ArrayBuffer)')
		})
	}
	if(axResp.status === 304) {
		return err({
			status: SyncHttpReqResultStatus.NotModified,
		})
	}
	if(axResp.status === 400) {
		return err({
			status: SyncHttpReqResultStatus.BadRequest,
			error: serializeFusionError('syncHttpReq', decodeHttpErrorMessage(axResp.data)),
		})
	}
	if(axResp.status === 401) {
		return err({
			status: SyncHttpReqResultStatus.Unauthorized,
		})
	}
	if(axResp.status === 403) {
		return err({
			status: SyncHttpReqResultStatus.Forbidden,
		})
	}
	// a bit of typing strangeness with the AxiosResponse headers...
	let etag = axResp.headers['etag'] ?? null
	if(!isNullableStringResponseHeader(etag)) {
		return err({
			status: SyncHttpReqResultStatus.Unknown,
			error: serializeFusionError('syncHttpReq', '!isNullableStringResponseHeader(etag)')
		})
	}
	let contentType = axResp.headers['content-type'] ?? null
	if(!isNullableStringResponseHeader(contentType)) {
		return err({
			status: SyncHttpReqResultStatus.Unknown,
			error: serializeFusionError('syncHttpReq', '!isNullableStringResponseHeader(contentType)')
		})
	}
	return ok({
		data: axResp.data,
		etag,
		contentType,
		axResp,
	})
}

// the api serializes rejections as {"Message": "..."}
// (api/src/lib/errorhandler/HttpError.go).  this text is diagnostic detail for
// the log, not something to put in front of a user— callers decide what to say
// about a bad request (see handleApiError.ts)
const decodeHttpErrorMessage = (data: ArrayBuffer): string => {
	try {
		const parsed = JSON.parse(new TextDecoder().decode(data))
		if(_.isString(parsed?.Message)) {
			return parsed.Message
		}
	} catch {
		// body wasn't the json we expected; nothing to add
	}
	return 'bad request (no message)'
}

const createCancel = (timeoutMs: number) => {
	const controller = new AbortController()
	let timer: ReturnType<typeof setTimeout>
	const reset = () => {
		clearTimeout(timer)
		timer = setTimeout(() => controller.abort('timeout'), timeoutMs)
	}
	reset() // start the timer immediately
	return { signal: controller.signal, reset }
}

// a bit of typing strangeness with the AxiosResponse headers...
const isNullableStringResponseHeader = (value: AxiosHeaderValue): value is string|null => {
	return value === null || _.isString(value)
}
